import React, { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { CustomBreadcrumb, CustomInput, CustomButton, CustomModal, CustomSearchableSelect } from '@/shared/components/atoms';
import { CustomCommonScreenHeader, CustomTable, CustomTableColumn, ConfirmationModal } from '@/shared/components/molecules';
import { Home, BookOpenText, Trash2, Upload, X, FileText, Calendar, Search } from 'lucide-react';
import { knowledgeHubApi } from '../api/knowledgeHubApi';
import { KnowledgeBaseFile } from '../types';
import { departmentApi } from '@/features/school-admin/api/departmentApi';
import { Department } from '@/features/school-admin/types';
import { useToastStore } from '@/shared/stores/toastStore';
import { useAuthStore } from '@/shared/stores/authStore';

const uploadSchema = z.object({
    title: z.string().min(2, 'Title must be at least 2 characters'),
    department_id: z.string().min(1, 'Please select a department'),
});

type UploadFormData = z.infer<typeof uploadSchema>;

export const KnowledgeHub = () => {
    const { user } = useAuthStore();
    const { showToast } = useToastStore();
    const [files, setFiles] = useState<KnowledgeBaseFile[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [fileToDelete, setFileToDelete] = useState<KnowledgeBaseFile | null>(null);

    // Upload state
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [page, setPage] = useState(1);
    const pageSize = 10;
    const fileInputRef = useRef<HTMLInputElement>(null);

    const schoolSlug = user?.school?.slug || '';

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors },
    } = useForm<UploadFormData>({
        resolver: zodResolver(uploadSchema),
    });

    useEffect(() => {
        if (schoolSlug) {
            fetchFiles();
            fetchDepartments();
        }
    }, [schoolSlug]);

    const fetchDepartments = async () => {
        try {
            const response = await departmentApi.getDepartments(schoolSlug, { page: 1, page_size: 50 });
            if (response.success && response.data) {
                const data = response.data as any;
                setDepartments(Array.isArray(data) ? data : (data.items || []));
            }
        } catch (error) {
            console.error('Failed to load departments', error);
        }
    };

    const fetchFiles = async () => {
        setIsLoading(true);
        try {
            const response = await knowledgeHubApi.getFiles(schoolSlug);
            if (response.success && response.data) {
                setFiles(response.data.items);
            }
        } catch (error) {
            showToast('Failed to load knowledge hub files', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const MAX_SIZE = 5 * 1024 * 1024; // 5MB
            if (file.size > MAX_SIZE) {
                showToast('File size exceeds 5MB limit', 'error');
                if (fileInputRef.current) fileInputRef.current.value = '';
                return;
            }
            setSelectedFile(file);
        }
    };

    const removeFile = () => {
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const file = e.dataTransfer.files?.[0];
        if (file) {
            const MAX_SIZE = 5 * 1024 * 1024; // 5MB
            if (file.size > MAX_SIZE) {
                showToast('File size exceeds 5MB limit', 'error');
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleUploadSubmit = async (data: UploadFormData) => {
        if (!schoolSlug || !selectedFile) {
            if (!selectedFile) showToast('Please select a file to upload', 'error');
            return;
        }
        setIsSubmitting(true);

        const response = await knowledgeHubApi.uploadFile(schoolSlug, {
            title: data.title,
            file: selectedFile,
            department_id: data.department_id
        });

        if (response.success) {
            showToast('File uploaded successfully', 'success');
            await fetchFiles();
            setIsUploadModalOpen(false);
            setSelectedFile(null);
            reset();
        } else {
            showToast(response.error || 'Failed to upload file', 'error');
        }
        setIsSubmitting(false);
    };

    const handleDeleteClick = (file: KnowledgeBaseFile) => {
        setFileToDelete(file);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (fileToDelete && schoolSlug) {
            setIsDeleting(true);
            const response = await knowledgeHubApi.deleteFile(schoolSlug, fileToDelete.id);
            if (response.success) {
                setFiles(prev => prev.filter(f => f.id !== fileToDelete.id));
                showToast('File deleted successfully', 'success');
            } else {
                showToast(response.error || 'Failed to delete file', 'error');
            }
            setIsDeleting(false);
            setIsDeleteModalOpen(false);
            setFileToDelete(null);
        }
    };

    const getFileTypeDetails = (fileType?: string) => {
        const type = fileType?.toLowerCase();
        switch (type) {
            case 'pdf':
                return { icon: <FileText size={18} />, color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' };
            case 'docx':
            case 'doc':
                return { icon: <FileText size={18} />, color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)' };
            case 'txt':
                return { icon: <FileText size={18} />, color: '#6b7280', bg: 'rgba(107, 114, 128, 0.1)' };
            default:
                return { icon: <FileText size={18} />, color: 'var(--accent-primary)', bg: 'var(--bg-secondary)' };
        }
    };

    const columns: CustomTableColumn<KnowledgeBaseFile>[] = [
        {
            header: 'Title',
            key: 'title',
            width: '40%',
            render: (file) => {
                const { icon, color, bg } = getFileTypeDetails(file.file_type);
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: bg,
                            color: color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            {icon}
                        </div>
                        <div>
                            <div style={{ fontWeight: '500' }}>{file.title}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{file.file_path.split(/\\|\//).pop()}</div>
                        </div>
                    </div>
                );
            }
        },
        {
            header: 'Department',
            key: 'department',
            width: '20%',
            render: (file) => (
                <span style={{ fontSize: '0.875rem', color: file.departments?.name ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                    {file.departments?.name || '—'}
                </span>
            )
        },
        {
            header: 'Uploaded On',
            key: 'created_at',
            width: '25%',
            render: (file) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    <Calendar size={14} />
                    {new Date(file.created_at).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    })}
                </div>
            )
        },
        {
            header: 'Actions',
            key: 'actions',
            align: 'right',
            minWidth: '100px',
            width: '100px',
            render: (file) => (
                <div className="table-action-cell" style={{ justifyContent: 'flex-end' }}>
                    <button
                        className="table-action-btn danger"
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(file); }}
                        title="Delete File"
                    >
                        <Trash2 size={18} />
                    </button>
                </div>
            )
        }
    ];

    const totalCount = files.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const paginatedFiles = files.slice((page - 1) * pageSize, page * pageSize);

    return (
        <div className="page-container">
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Knowledge Hub', icon: <BookOpenText size={14} /> }
                ]}
            />
            <CustomCommonScreenHeader
                title="Knowledge Hub"
                subtitle="Upload school handbooks, policies, and documents to power your AI assistant."
                icon={<BookOpenText size={24} />}
                actionLabel="Upload Document"
                onActionClick={() => setIsUploadModalOpen(true)}
            />

            {!isLoading && files.length === 0 ? (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '4rem 1.5rem',
                    background: 'var(--card-bg)',
                    borderRadius: '16px',
                    border: '1px dashed var(--border-color)',
                    marginTop: '1.5rem',
                    textAlign: 'center'
                }}>
                    <div style={{
                        width: '72px',
                        height: '72px',
                        borderRadius: '50%',
                        background: 'var(--bg-secondary)',
                        color: 'var(--accent-primary)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '1.25rem'
                    }}>
                        <BookOpenText size={36} />
                    </div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                        No Knowledge Base Documents
                    </h3>
                    <p style={{ color: 'var(--text-muted)', maxWidth: '400px', marginBottom: '1.5rem', lineHeight: '1.5', fontSize: '0.875rem' }}>
                        Your AI assistant needs information to help parents. Upload handbooks, fee structures, or policies to get started.
                    </p>
                    <CustomButton
                        variant="filled"
                        color="primary"
                        onClick={() => setIsUploadModalOpen(true)}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Upload size={18} />
                            <span>Upload Your First Document</span>
                        </div>
                    </CustomButton>
                </div>
            ) : (
                <div style={{ marginTop: '1.25rem' }}>
                    <CustomTable
                        columns={columns}
                        data={paginatedFiles}
                        isLoading={isLoading}
                        isCompact={true}
                        emptyMessage="No documents found"
                        pagination={{
                            currentPage: page,
                            totalPages: totalPages,
                            totalCount: totalCount,
                            pageSize: pageSize,
                            onPageChange: setPage,
                            hasPrevious: page > 1,
                            hasNext: page < totalPages,
                            isLoading: isLoading,
                            itemName: 'documents',
                        }}
                    />
                </div>
            )}


            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Document"
                message={`Are you sure you want to delete "${fileToDelete?.title}"? This will remove it from the AI's knowledge base.`}
                confirmLabel={isDeleting ? "Deleting..." : "Delete"}
                variant="danger"
                isLoading={isDeleting}
            />

            {/* Upload Modal */}
            <CustomModal
                isOpen={isUploadModalOpen}
                onClose={() => {
                    setIsUploadModalOpen(false);
                    setSelectedFile(null);
                    reset();
                }}
                title="Upload Document"
                size="medium"
            >
                <form onSubmit={handleSubmit(handleUploadSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <CustomInput
                        label="Document Title"
                        placeholder="e.g. Student Handbook 2024"
                        error={errors.title?.message}
                        {...register('title')}
                        required
                    />

                    <CustomSearchableSelect
                        label="Department"
                        options={departments.map(d => ({ label: d.name, value: d.id }))}
                        value={watch('department_id') || ''}
                        onChange={(val) => setValue('department_id', val as string, { shouldValidate: true })}
                        placeholder="Search and select department..."
                        error={errors.department_id?.message}
                        hideAvatar
                        required
                    />

                    <div>
                        <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block' }}>
                            Select File <span style={{ color: 'var(--status-error)' }}>*</span>
                        </label>

                        {selectedFile ? (
                            <div style={{
                                padding: '1rem',
                                border: '1px solid var(--border-color)',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                background: 'var(--bg-secondary)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <FileText size={20} color="var(--accent-primary)" />
                                    <div>
                                        <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{selectedFile.name}</div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</div>
                                    </div>
                                </div>
                                <button type="button" onClick={removeFile} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                                    <X size={18} />
                                </button>
                            </div>
                        ) : (
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                style={{
                                    padding: '2rem',
                                    border: `2px dashed ${isDragging ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                                    borderRadius: '8px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    cursor: 'pointer',
                                    color: 'var(--text-muted)',
                                    transition: 'all 0.2s',
                                    background: isDragging ? 'rgba(var(--accent-primary-rgb), 0.05)' : 'transparent'
                                }}
                                onMouseOver={(e) => !isDragging && (e.currentTarget.style.borderColor = 'var(--accent-primary)')}
                                onMouseOut={(e) => !isDragging && (e.currentTarget.style.borderColor = 'var(--border-color)')}
                            >
                                <Upload size={32} />
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#3b82f6' }}>Click to upload or drag and drop</div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>PDF, DOCX or TXT (Max 5MB)</div>
                                </div>
                            </div>
                        )}
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            accept=".pdf,.docx,.txt"
                            style={{ display: 'none' }}
                        />
                    </div>

                    <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                        <CustomButton
                            type="button"
                            variant="outline"
                            color="secondary"
                            onClick={() => { setIsUploadModalOpen(false); setSelectedFile(null); reset(); }}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </CustomButton>
                        <CustomButton
                            type="submit"
                            variant="filled"
                            color="primary"
                            loading={isSubmitting}
                            disabled={!selectedFile}
                        >
                            Upload & Process
                        </CustomButton>
                    </div>
                </form>
            </CustomModal>
        </div>
    );
};
